import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Save } from 'lucide-react';
import api from '../../api/axios';
import toast from 'react-hot-toast';

const PERMISSION_OPTIONS: { key: string; label: string }[] = [
  { key: 'orders', label: 'Orders' },
  { key: 'products', label: 'Products' },
  { key: 'categories', label: 'Categories' },
  { key: 'inventory', label: 'Inventory' },
  { key: 'discounts', label: 'Discounts' },
  { key: 'shipping', label: 'Shipping' },
  { key: 'reviews', label: 'Reviews' },
  { key: 'newsletter', label: 'Newsletter' },
  { key: 'campaigns', label: 'Campaigns' },
  { key: 'contact', label: 'Contact Messages' },
  { key: 'payments', label: 'Payments' },
  { key: 'logs', label: 'Activity Logs' },
];

const StaffAdminPage: React.FC = () => {
  const [staff, setStaff] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingPermissions, setEditingPermissions] = useState<string | null>(null);
  const [tempPermissions, setTempPermissions] = useState<string[]>([]);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    permissions: [] as string[],
  });

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    try {
      setIsLoading(true);
      const res = await api.get('/api/admin/staff');
      if (res.data.success) setStaff(res.data.data || []);
    } catch {
      toast.error('Failed to fetch staff accounts');
    } finally {
      setIsLoading(false);
    }
  };

  const togglePermission = (perms: string[], key: string): string[] =>
    perms.includes(key) ? perms.filter((p) => p !== key) : [...perms, key];

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.permissions.length === 0) {
      toast.error('Select at least one permission for this staff member');
      return;
    }
    try {
      setIsSubmitting(true);
      await api.post('/api/admin/staff', formData);
      toast.success('Staff account created');
      setShowForm(false);
      setFormData({ email: '', password: '', first_name: '', last_name: '', phone: '', permissions: [] });
      fetchStaff();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to create staff account');
    } finally {
      setIsSubmitting(false);
    }
  };

  const startEditPermissions = (member: any) => {
    setEditingPermissions(member.id);
    setTempPermissions(member.permissions || []);
  };

  const savePermissions = async (id: string) => {
    try {
      await api.put(`/api/admin/staff/${id}/permissions`, { permissions: tempPermissions });
      toast.success('Permissions updated');
      setEditingPermissions(null);
      fetchStaff();
    } catch {
      toast.error('Failed to update permissions');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Remove this staff account? They will no longer be able to log in.')) return;
    try {
      await api.delete(`/api/admin/staff/${id}`);
      toast.success('Staff account removed');
      fetchStaff();
    } catch {
      toast.error('Failed to remove staff account');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs tracking-widest uppercase text-nude mb-1">
            Super Admin
          </p>
          <h1 className="font-serif text-3xl text-primary">Staff Accounts</h1>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus size={14} />
          <span>New Staff Account</span>
        </button>
      </div>

      <p className="text-xs text-nude leading-relaxed max-w-2xl">
        Staff accounts (Cashier, Manager, or any custom role) can log in and access only the admin sections you grant them below. Regular Admin and Super Admin accounts always have full access and are managed separately under Users.
      </p>

      {showForm && (
        <form onSubmit={handleCreate} className="bg-white border border-light p-6 space-y-6">
          <h2 className="font-serif text-lg text-primary">New Staff Account</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs tracking-widest uppercase text-nude block mb-2">First Name</label>
              <input
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                className="input-luxury"
                required
              />
            </div>
            <div>
              <label className="text-xs tracking-widest uppercase text-nude block mb-2">Last Name</label>
              <input
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                className="input-luxury"
                required
              />
            </div>
            <div>
              <label className="text-xs tracking-widest uppercase text-nude block mb-2">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input-luxury"
                required
              />
            </div>
            <div>
              <label className="text-xs tracking-widest uppercase text-nude block mb-2">Phone (Optional)</label>
              <input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="input-luxury"
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs tracking-widest uppercase text-nude block mb-2">Temporary Password</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="input-luxury"
                minLength={8}
                required
              />
            </div>
          </div>

          <div>
            <p className="text-xs tracking-widest uppercase text-nude mb-3">
              Grant Access To
            </p>
            <div className="grid grid-cols-3 gap-3">
              {PERMISSION_OPTIONS.map((perm) => (
                <label key={perm.key} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.permissions.includes(perm.key)}
                    onChange={() => setFormData({
                      ...formData,
                      permissions: togglePermission(formData.permissions, perm.key),
                    })}
                    className="accent-primary"
                  />
                  <span className="text-xs">{perm.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex space-x-3">
            <button type="submit" disabled={isSubmitting} className="btn-primary disabled:opacity-50">
              {isSubmitting ? 'Creating...' : 'Create Staff Account'}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="bg-white border border-light overflow-x-auto">
        {isLoading ? (
          <div className="p-6 space-y-4">
            {[1, 2, 3].map((i) => <div key={i} className="skeleton h-12 rounded" />)}
          </div>
        ) : staff.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-xs tracking-widest uppercase text-nude">
              No staff accounts yet
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-light">
                {['Name', 'Email', 'Permissions', 'Actions'].map((h) => (
                  <th key={h} className="px-6 py-3 text-left text-xs tracking-widest uppercase text-nude">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {staff.map((member) => (
                <tr key={member.id} className="border-b border-light hover:bg-light/50 transition-colors align-top">
                  <td className="px-6 py-4 text-xs font-medium tracking-wide whitespace-nowrap">
                    {member.first_name} {member.last_name}
                  </td>
                  <td className="px-6 py-4 text-xs text-nude whitespace-nowrap">
                    {member.email}
                  </td>
                  <td className="px-6 py-4">
                    {editingPermissions === member.id ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                          {PERMISSION_OPTIONS.map((perm) => (
                            <label key={perm.key} className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={tempPermissions.includes(perm.key)}
                                onChange={() => setTempPermissions(togglePermission(tempPermissions, perm.key))}
                                className="accent-primary"
                              />
                              <span className="text-xs">{perm.label}</span>
                            </label>
                          ))}
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => savePermissions(member.id)}
                            className="text-green-600 hover:text-green-700 transition-colors flex items-center space-x-1 text-xs"
                          >
                            <Save size={12} />
                            <span>Save</span>
                          </button>
                          <button
                            onClick={() => setEditingPermissions(null)}
                            className="text-nude hover:text-primary transition-colors text-xs"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {(member.permissions || []).length === 0 ? (
                          <span className="text-xs text-amber-600">No access granted</span>
                        ) : (
                          member.permissions.map((p: string) => (
                            <span key={p} className="text-[10px] tracking-wide uppercase bg-light px-2 py-1 text-secondary">
                              {PERMISSION_OPTIONS.find((o) => o.key === p)?.label || p}
                            </span>
                          ))
                        )}
                        <button
                          onClick={() => startEditPermissions(member)}
                          className="text-[10px] tracking-widest uppercase text-primary hover:text-nude transition-colors ml-1"
                        >
                          Edit
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleDelete(member.id)}
                      className="text-red-500 hover:text-red-700 transition-colors"
                      title="Remove staff account"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default StaffAdminPage;